import NextApp, { AppContext } from 'next/app';
import { IncomingMessage } from 'http';

import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';

import { Wrapper } from '@/components/_shared/Wrapper';
import { IntlProviderWrapper } from '@/providers/IntlProviders';
import { QueryClientProvider } from '@/providers/QueryClientProvider';
import { ListingsAppProps } from '@/types';
import { getDefaultDomainUrl, getDomainUrl } from '@/utils/utils';

import { useAWSRum } from '../../common/monitoring/awsrum';
import { DatadogInit } from '../../common/monitoring/datadogrum';

import '@/styles/globals.css';

export default function App({
  Component,
  pageProps,
  serverUrl,
  stage,
  rumGuestRoleArn,
  identityPoolId,
  applicationIds,
}: ListingsAppProps) {
  useAWSRum({
    rumGuestRoleArn,
    identityPoolId,
    applicationIds,
  });

  const { locale = 'en-US', messages = {} } = pageProps;

  return (
    <QueryClientProvider>
      <IntlProviderWrapper messages={messages} locale={locale}>
        <Wrapper serverUrl={serverUrl} stage={stage} locale={locale}>
          <DatadogInit stage={stage} />
          <ConsentManagerScripts />
          <Component {...pageProps} stage={stage} />
        </Wrapper>
      </IntlProviderWrapper>
    </QueryClientProvider>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const req = context.ctx.req as IncomingMessage;
  const appProps = await NextApp.getInitialProps(context);

  // Extract serverUrl using robust getDomainUrl function
  const serverUrl = getDomainUrl(req) || getDefaultDomainUrl();

  // Get stage from environment variable
  const stage = stageFromString(process.env.STAGE || Stage.TEST);

  // Add RUM configuration - these should come from environment variables or CDK outputs
  const rumGuestRoleArn = process.env.RUM_GUEST_ROLE_ARN || '';
  const identityPoolId = process.env.IDENTITY_POOL_ID || '';
  const applicationIds = process.env.APPLICATION_IDS ? JSON.parse(process.env.APPLICATION_IDS) : {};

  return {
    ...appProps,
    serverUrl,
    stage,
    rumGuestRoleArn,
    identityPoolId,
    applicationIds,
  };
};
